import { get, sumBy } from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import disqus from '@/components/disqus/disqus.vue';
import marketCharts from '@/components/market/market-detail-charts/market-detail-charts.vue';
import marketContractHorizontalV2 from '@/components/market/v2/market-contract-horizontal-v2/market-contract-horizontal-v2.vue';
import marketHistory from '@/components/market/market-history/market-history.vue';
import marketHeaderTitle from '@/components/market/market-header-title/market-header-title.vue';
import marketHeaderTitleLarge from '@/components/market/market-header-title-large/market-header-title-large.vue';
import marketPayout from '@/components/market/market-payout/market-payout.vue';
import marketRelated from '@/components/market/market-related/market-related.vue';
import purchaseDesktop from '@/components/purchase/purchase-desktop/purchase-desktop.vue';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import marketRules from '@/components/market/market-rules/market-rules.vue';
import tabs from '@/components/generics/tabs/tabs.vue';
import headerTagsMixin from '@/mixins/headerTags';
import { contractOwnershipNum, isMarketSingle, isMarketSettled, hasMarketStatusMessage, hasMarketPayout } from '@/utils/helpers';
import Contract from '@/models/contract';
import { MODALS } from '@/components/app/app-modals/app-modals';
import { isIos } from '@/mixins/userAgent';
import scrollTo from '@/utils/scrollTo';
import { waitForStatusReady } from '@/utils/mutex';

const DEFAULT_PAGE_TITLE = 'PredictIt';

export default {
  mixins: [
    headerTagsMixin,
  ],
  components: {
    alertBox,
    marketHeaderTitle,
    marketHeaderTitleLarge,
    marketCharts,
    marketContract: marketContractHorizontalV2,
    marketHistory,
    marketPayout,
    marketRelated,
    disqus,
    purchaseDesktop,
    marketRules,
    tabs,
  },
  props: {
    showRules: {
      type: Boolean,
      default: false,
    },
    marketId: {
      type: Number,
      required: true,
    },
    prerendered: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hasMarketStatusMessage,
      ready: false,
      shownOnInit: false,
      isHistoryOpen: false,
      isRelatedOpen: true,
      reinitializeAttempt: 1,
      showAllContracts: false,
      activeTab: null,
      showHistory: false,
    };
  },
  metaInfo() {
    if (this.market && this.market.marketId) {
      return {
        title: this.market.seoTitle,
        link: [
          { rel: 'canonical', href: `${window.location.href}` },
        ],
        meta: [
          { name: 'description', content: this.market.seoDescription },
          { itemprop: 'name', content: this.market.seoTitle },
          { itemprop: 'description', content: this.market.seoDescription },
          { itemprop: 'image', content: this.market.imageName },
          { property: 'og:title', content: this.market.seoTitle },
          { property: 'og:type', content: 'article' },
          { property: 'og:image', content: this.market.imageName },
          { property: 'og:description', content: this.market.seoDescription },
          { property: 'og:site_name', content: 'Predictit' },
          { property: 'og:url', content: `${window.location.href}` },
        ],
      };
    }
    return {};
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapState('app', ['previousRoute']),
    ...mapState('market', [
      'market', 'contracts', 'marketLoadError',
      'marketIsEngineBusy', 'marketIsEngineBusyMessage',
    ]),
    ...mapState('trade', ['tradeOptions']),
    ...mapState('popup', ['currentPopup']),
    ...mapState('notifications', ['lastConfirmedContractData']),
    ...mapState('user', ['status']),
    isSettled() {
      return this.market && isMarketSettled(this.market);
    },
    isPayoutAvailable() {
      return this.isLoggedIn && hasMarketPayout(this.market) && !this.isSettled;
    },
    historyKey() {
      return `${this.marketKey()}-${this.reinitializeAttempt.toString()}`;
    },
    showMarketHistory() {
      return this.market.userHasTradeHistory || this.showHistory;
    },
    title() {
      return `Market details for ${this.market.symbol}--${this.market.marketId}`;
    },
    getLayout() {
      return this.isLayoutDesktop ? 'list' : 'grid';
    },
    visibleContracts() {
      return this.showAllContracts ? this.contracts : this.notHiddenContracts;
    },
    notHiddenContracts() {
      return this.contracts.filter(i => !i.hiddenByDefault);
    },
    hiddenContracts() {
      return this.contracts.filter(i => i.hiddenByDefault);
    },
    hiddenContractsOffersOrSharesCount() {
      if (this.hiddenContracts.length > 0) {
        return sumBy(this.hiddenContracts, contractOwnershipNum);
      }
      return 0;
    },
    shouldShowToggleMoreButton() {
      return this.hiddenContracts.length > 0;
    },
    classModifiers() {
      return {
        'market-detail': true,
        'market-detail--single': this.isMarketSingle,
      };
    },
    toggleMoreModifiers() {
      return {
        'market-detail__contracts-toggle-more': true,
        'market-detail__contracts-toggle-more--offers-or-shares': this.hiddenContractsOffersOrSharesCount > 0,
        'market-detail__contracts-toggle-more--active': this.showAllContracts,
      };
    },
    showEngineBusyNotification() {
      return (
        (this.market.isEngineBusy && this.market.isEngineBusyMessage) ||
        (this.marketIsEngineBusy && this.marketIsEngineBusyMessage)
      );
    },
    engineBusyNotificationContent() {
      return this.market.isEngineBusyMessage || this.marketIsEngineBusyMessage;
    },
    statusMessage() {
      return this.market.isOpenStatusMessage;
    },
    statusMessageLevel() {
      if (this.statusMessage === 'This market has settled.') return 'danger';
      return 'warning';
    },
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    shouldInitializePurchaseOnInit() {
      return this.status.hasBeenFunded === true &&
        this.isMarketSingle &&
        !this.shownOnInit &&
        this.isLayoutDesktop &&
        this.isLoggedIn;
    },
    ogImageSrc() {
      return this.prerendered !== '' && this.market && this.market.imageName;
    },
    showTitleBackButton() {
      return isIos && this.previousRoute !== null;
    },
    showEconomicIndicator() {
      return [2, 3, 4].includes(this.market.econIndicatorTradeStatus);
    },
  },
  methods: {
    ...mapActions('insight', ['getMarketFeeds']),
    ...mapActions('market', [
      'getMarket', 'clearMarket', 'clearMarketEngineBusyMessage',
    ]),
    ...mapActions('popup', ['setCurrentPopup', 'setTradePopup']),
    ...mapActions('trade', ['setTradeOptions', 'clearOrderBook']),
    openContractIfPassedToQueryString() {
      if (this.isMarketSingle) return;
      const contractId = parseInt(this.$route.query.contractId, 10);
      const inContracts = this.contracts.map(i => i.contractId).includes(contractId);
      const inHiddenContracts = this.hiddenContracts.map(i => i.contractId).includes(contractId);
      if (!inContracts) return;

      if (inHiddenContracts) {
        this.showAllContracts = true;
      }

      this.$nextTick(() => {
        const $el = document.querySelector(`[data-contract-id="${contractId}"]`);
        const destination = Math.max(0, $el.offsetTop - 160);
        scrollTo(destination, 1000, 'linear', () => {
          if (!this.isLoggedIn) return;
          const timeout = this.isLayoutMobile ? 2500 : 0;
          setTimeout(() => {
            $el.click();
          }, timeout);
        });
      });
    },
    marketKey(name = 'market-details') {
      const loggedIn = (this.isLoggedIn) ? 'logged-in' : 'logged-out';
      return this.market ? `${name}-${this.market.marketId}-${loggedIn}` : '';
    },
    onPurchaseShownOnInit() {
      this.shownOnInit = true;
    },
    initializePurchase() {
      if (
        this.isLayoutMobile &&
        this.tradeOptions !== null &&
        this.tradeOptions.contractId !== null
      ) {
        if (!this.isLoggedIn && this.currentPopup !== MODALS.LOGIN) {
          this.setCurrentPopup(MODALS.LOGIN);
        } else if (this.currentPopup !== MODALS.PURCHASE) {
          this.setTradePopup({
            name: MODALS.PURCHASE,
            options: {
              market: this.market,
            },
          });
        }
      } else if (
        !this.isLoggedIn &&
        this.tradeOptions !== null &&
        this.tradeOptions.contractId !== null &&
        this.isPurchaseDesktopMode(this.tradeOptions.contractId) &&
        this.currentPopup !== MODALS.LOGIN
      ) {
        this.setCurrentPopup(MODALS.LOGIN);
      }
    },
    contractKey(prefix, data) {
      const contract = new Contract(data);
      return `${prefix}-${this.marketKey()}-${contract.contractId}-${contract.mode}-${this.reinitializeAttempt.toString()}-${this.isLayoutMobile.toString()}`;
    },
    retrieveMarketData(marketId) {
      this.clearOrderBook();
      this.clearMarket();
      if (marketId) {
        const commentThreadId = this.$route.query.commentThreadId;
        return this.getMarket({ marketId, commentThreadId })
          .then((data) => {
            if (data.econIndicatorTradeStatus === 3) {
              this.setCurrentPopup(MODALS.AFFILIATIONS);
            }
          });
      }
      return Promise.resolve(1);
    },
    isPurchaseDesktopMode(contractId) {
      return this.isLayoutDesktop && this.tradeOptions &&
        this.tradeOptions.contractId === contractId;
    },
    onToggleMore() {
      this.showAllContracts = !this.showAllContracts;
      if (!this.showAllContracts) {
        window.scrollTo(0, 0);
      }
    },
    onToggleHistory() {
      this.isHistoryOpen = !this.isHistoryOpen;
      this.isRelatedOpen = false;
    },
    onToggleRelated() {
      this.isRelatedOpen = !this.isRelatedOpen;
      this.isHistoryOpen = false;
    },
    loadHandler(value) {
      if (value) {
        this.ready = false;
        this.retrieveMarketData(this.marketId)
          .then(waitForStatusReady)
          .then(() => {
            if (!this.status.hasBeenFunded) {
              this.setTradeOptions(null);
            }
          })
          .then(() => {
            this.ready = true;
            this.reorganizeTags();
            this.initializePurchase();
            this.openContractIfPassedToQueryString();
          });
      } else {
        this.retrieveMarketData(this.marketId)
          .then(() => {
            this.ready = true;
            this.reorganizeTags();
            this.initializePurchase();
            this.openContractIfPassedToQueryString();
          });
      }
    },
  },
  watch: {
    lastConfirmedContractData(data) {
      // showing history on succesfull match
      const contractIds = (this.contracts || []).map(i => i.contractId);
      if (data && data.matched && contractIds.includes(data.id)) {
        this.showHistory = true;
      }
    },
    tradeOptions() {
      this.initializePurchase();
    },
    isLoggedIn: {
      handler: 'loadHandler',
      immediate: true,
    },
    $route(to, from) {
      const dontReload = get(to, 'params.dontReload', false);
      if ((from.fullPath !== to.fullPath) && !dontReload) {
        this.shownOnInit = false;
        this.retrieveMarketData(to.params.marketId)
          .then(() => {
            this.reorganizeTags();
          });
      }
    },
  },
  beforeDestroy() {
    this.clearMarketEngineBusyMessage();
    this.setTradeOptions(null);
    document.title = DEFAULT_PAGE_TITLE;
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-market-detail/page-market-detail.js